/**
 * BreakdownExplorer — slice performance by any dimension (symbol, side, exit,
 * timeframe, strategy, strength, hold, day, hour). Sortable table with an
 * inline P&L bar; low-sample rows are dimmed. Clicking a row drills the Trades
 * Explorer below to that slice.
 */
import React, { useMemo, useState } from 'react';
import {
  Box, Card, CardContent, CardHeader, Typography, ToggleButton, ToggleButtonGroup,
  Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, Tooltip,
} from '@mui/material';
import { useAppTheme } from '../../hooks/useAppTheme';
import { AnalyticsSummary, DimRow } from '../../services/analytics.service';
import { formatCurrency } from '../../utils/formatters';

type NRow = DimRow & { filterValue: string };
interface DimDef { id: string; label: string; backendDim: string | null; rows: NRow[] }

interface Props {
  data: AnalyticsSummary;
  onSelect: (sel: { dim: string; value: string; label: string } | null) => void;
  active: { dim: string; value: string } | null;
}

const BreakdownExplorer: React.FC<Props> = ({ data, onSelect, active }) => {
  const { C } = useAppTheme();
  const [dimId, setDimId] = useState('symbol');
  const [sortKey, setSortKey] = useState<keyof DimRow>('totalPnl');
  const [asc, setAsc] = useState(false);

  const dims: DimDef[] = useMemo(() => {
    const asN = (rows: DimRow[]): NRow[] => rows.map(r => ({ ...r, filterValue: r.key }));
    const stratRows: NRow[] = data.byStrategy.map(s => ({
      key: s.name, trades: s.totalTrades, wins: 0, losses: 0, winRate: s.winRate, avgR: s.avgR,
      totalPnl: s.totalPnl, avgPnl: s.totalTrades ? s.totalPnl / s.totalTrades : 0,
      enoughData: s.totalTrades >= 8, filterValue: String(s.strategyId ?? 'manual'),
    }));
    // Tag breakdown — backend computes data.tagBreakdown but it wasn't surfaced here.
    const tagRows: NRow[] = (data.tagBreakdown || []).map(t => ({
      key: t.tag, trades: t.totalTrades, wins: 0, losses: 0, winRate: t.winRate, avgR: t.avgR,
      totalPnl: t.totalPnl, avgPnl: t.totalTrades ? t.totalPnl / t.totalTrades : 0,
      enoughData: t.totalTrades >= 8, filterValue: t.tag,
    }));
    return [
      { id: 'symbol', label: 'Symbol', backendDim: 'symbol', rows: asN(data.bySymbol) },
      { id: 'side', label: 'Side', backendDim: 'side', rows: asN(data.bySide) },
      { id: 'exit', label: 'Exit', backendDim: 'exit', rows: asN(data.byExitMethod) },
      { id: 'timeframe', label: 'Timeframe', backendDim: 'timeframe', rows: asN(data.byTimeframe) },
      { id: 'strategy', label: 'Strategy', backendDim: 'strategy', rows: stratRows },
      { id: 'strength', label: 'Strength', backendDim: 'strength', rows: asN(data.byStrength) },
      ...(tagRows.length ? [{ id: 'tag', label: 'Tag', backendDim: 'tag', rows: tagRows }] : []),
      { id: 'day', label: 'Day', backendDim: 'day', rows: asN(data.byDayOfWeek) },
      { id: 'hold', label: 'Hold', backendDim: null, rows: asN(data.byHoldBucket) },
      { id: 'hour', label: 'Hour', backendDim: null, rows: asN(data.byHour) },
    ];
  }, [data]);

  const dim = dims.find(d => d.id === dimId) || dims[0];
  const rows = useMemo(() => {
    const sorted = [...dim.rows].sort((a, b) => {
      const av = a[sortKey] as number, bv = b[sortKey] as number;
      return asc ? av - bv : bv - av;
    });
    return sorted.slice(0, 25);
  }, [dim, sortKey, asc]);

  const maxAbsPnl = Math.max(1, ...rows.map(r => Math.abs(r.totalPnl)));
  const clickable = !!dim.backendDim;

  const onSort = (k: keyof DimRow) => {
    if (sortKey === k) setAsc(!asc); else { setSortKey(k); setAsc(false); }
  };

  const head = (k: keyof DimRow, label: string, align: 'left' | 'right' = 'right') => (
    <TableCell align={align} sx={{ py: 0.75, fontWeight: 700, fontSize: '0.72rem' }}>
      <TableSortLabel active={sortKey === k} direction={sortKey === k && asc ? 'asc' : 'desc'} onClick={() => onSort(k)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Card sx={{ borderRadius: 1.5 }}>
      <CardHeader
        title={<Typography fontWeight={700}>Performance breakdown</Typography>}
        subheader={<Typography variant="caption" color="text.secondary">
          {clickable ? 'Click a row to see its trades below' : 'Display-only dimension'} · dimmed = low sample (&lt;8)
        </Typography>}
        sx={{ pb: 0.5 }}
      />
      <CardContent>
        <ToggleButtonGroup
          size="small" exclusive value={dimId}
          onChange={(_, v) => { if (v) { setDimId(v); } }}
          sx={{ flexWrap: 'wrap', mb: 1.5, '& .MuiToggleButton-root': { px: 1.25, py: 0.25, fontSize: '0.7rem', fontWeight: 700, textTransform: 'none' } }}
        >
          {dims.map(d => <ToggleButton key={d.id} value={d.id}>{d.label}</ToggleButton>)}
        </ToggleButtonGroup>

        {rows.length === 0 ? (
          <Box sx={{ p: 2, borderRadius: 1.5, border: `1px dashed ${C.neutralBorder}`, bgcolor: C.neutralMuted }}>
            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
              No data for this dimension yet — it populates as trades that carry this attribute accumulate.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ py: 0.75, fontWeight: 700, fontSize: '0.72rem' }}>{dim.label}</TableCell>
                  {head('trades', 'Trades')}
                  {head('winRate', 'Win %')}
                  {head('avgR', 'Avg R')}
                  {head('totalPnl', 'Net P&L')}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(r => {
                  const isActive = active && active.dim === dim.backendDim && active.value === r.filterValue;
                  const barW = (Math.abs(r.totalPnl) / maxAbsPnl) * 100;
                  return (
                    <TableRow key={r.key}
                      hover={clickable}
                      onClick={() => clickable && onSelect(isActive ? null : { dim: dim.backendDim!, value: r.filterValue, label: `${dim.label}: ${r.key}` })}
                      sx={{
                        cursor: clickable ? 'pointer' : 'default',
                        opacity: r.enoughData ? 1 : 0.5,
                        bgcolor: isActive ? C.accentMuted : 'transparent',
                      }}>
                      <TableCell sx={{ py: 0.6, fontWeight: 700, fontSize: '0.78rem', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.key}{!r.enoughData && <Tooltip arrow title="Low sample — treat as indicative only"><span style={{ color: C.dim, fontSize: '0.65rem' }}> ⚑</span></Tooltip>}
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.6, fontVariantNumeric: 'tabular-nums', fontSize: '0.78rem' }}>{r.trades}</TableCell>
                      <TableCell align="right" sx={{ py: 0.6, fontVariantNumeric: 'tabular-nums', fontSize: '0.78rem', color: r.winRate >= 50 ? C.bull : 'text.primary' }}>{r.winRate.toFixed(0)}%</TableCell>
                      <TableCell align="right" sx={{ py: 0.6, fontVariantNumeric: 'tabular-nums', fontSize: '0.78rem', fontWeight: 700, color: r.avgR >= 0 ? C.bull : C.bear }}>{r.avgR >= 0 ? '+' : ''}{r.avgR.toFixed(2)}</TableCell>
                      <TableCell align="right" sx={{ py: 0.6 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.75 }}>
                          <Box sx={{ width: 60, height: 6, borderRadius: 3, bgcolor: 'action.hover', overflow: 'hidden', display: 'flex', justifyContent: r.totalPnl >= 0 ? 'flex-start' : 'flex-end' }}>
                            <Box sx={{ width: `${barW}%`, height: '100%', bgcolor: r.totalPnl >= 0 ? C.bull : C.bear }} />
                          </Box>
                          <Typography sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.78rem', fontWeight: 700, color: r.totalPnl >= 0 ? C.bull : C.bear, minWidth: 78, textAlign: 'right' }}>
                            {formatCurrency(r.totalPnl)}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default BreakdownExplorer;
